import { useFrame } from '@react-three/fiber';
import { useRef } from 'react';
import { BufferAttribute, DynamicDrawUsage, FrontSide, LineSegments, Matrix4, Vector3 } from 'three';

import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import type { Point3D } from 'api/models';

const FLOATS_PER_BOX = 72;
const VERTICES_PER_BOX = 24;

const corners = Array.from({ length: 8 }, () => new Vector3());

const positions = new Float32Array(FLOATS_PER_BOX);
const positionAttribute = new BufferAttribute(positions, 3);
positionAttribute.usage = DynamicDrawUsage;

function setBoxEdges(localMin: Vector3, localMax: Vector3, localToWorld: Matrix4, positions: Float32Array) {
    const min: Point3D = { x: localMin.x, y: localMin.y, z: localMin.z };
    const max: Point3D = { x: localMax.x, y: localMax.y, z: localMax.z };

    // transform corners to world space
    corners[0].set(min.x, min.y, min.z).applyMatrix4(localToWorld);
    corners[1].set(max.x, min.y, min.z).applyMatrix4(localToWorld);
    corners[2].set(min.x, max.y, min.z).applyMatrix4(localToWorld);
    corners[3].set(max.x, max.y, min.z).applyMatrix4(localToWorld);
    corners[4].set(min.x, min.y, max.z).applyMatrix4(localToWorld);
    corners[5].set(max.x, min.y, max.z).applyMatrix4(localToWorld);
    corners[6].set(min.x, max.y, max.z).applyMatrix4(localToWorld);
    corners[7].set(max.x, max.y, max.z).applyMatrix4(localToWorld);

    let i = 0;
    const write = (a: Vector3, b: Vector3) => {
        positions[i++] = a.x;
        positions[i++] = a.y;
        positions[i++] = a.z;
        positions[i++] = b.x;
        positions[i++] = b.y;
        positions[i++] = b.z;
    };

    // bottom face edges
    write(corners[0], corners[1]);
    write(corners[1], corners[3]);
    write(corners[3], corners[2]);
    write(corners[2], corners[0]);

    // top face edges
    write(corners[4], corners[5]);
    write(corners[5], corners[7]);
    write(corners[7], corners[6]);
    write(corners[6], corners[4]);

    // vertical edges
    write(corners[0], corners[4]);
    write(corners[1], corners[5]);
    write(corners[3], corners[7]);
    write(corners[2], corners[6]);
}

export const DraggingBox = () => {
    const draggingBoxRef = usePlacementStore((state) => state.draggingBoxRef);
    const lineSegmentsRef = useRef<LineSegments | undefined>(undefined);

    useFrame(() => {
        if (!lineSegmentsRef.current) {
            return;
        }

        const { isActive, boundingBox, localToWorld } = draggingBoxRef.current;

        if (!isActive || boundingBox.isEmpty()) {
            if (lineSegmentsRef.current.visible) {
                lineSegmentsRef.current.visible = false;
                lineSegmentsRef.current.geometry.setDrawRange(0, 0);
            }
            return;
        }

        setBoxEdges(boundingBox.min, boundingBox.max, localToWorld, positions);
        positionAttribute.needsUpdate = true;

        lineSegmentsRef.current.geometry.setDrawRange(0, VERTICES_PER_BOX);
        lineSegmentsRef.current.computeLineDistances();
        lineSegmentsRef.current.geometry.attributes.position.needsUpdate = true;
        lineSegmentsRef.current.geometry.computeBoundingSphere();
        lineSegmentsRef.current.visible = true;
    });

    const handleLineSegmentsRef = (lineSegments: LineSegments) => {
        if (lineSegments) {
            lineSegmentsRef.current = lineSegments;

            lineSegments.geometry.setAttribute('position', positionAttribute);
            lineSegments.geometry.setDrawRange(0, 0);
            lineSegments.geometry.attributes.position.needsUpdate = true;
        }
    };

    return (
        <lineSegments ref={handleLineSegmentsRef} frustumCulled={false} visible={false}>
            <bufferGeometry />
            <lineDashedMaterial
                color="#649fda"
                dashSize={0.15}
                depthTest={false}
                gapSize={0.06}
                opacity={0.9}
                side={FrontSide}
                transparent={true}
            />
        </lineSegments>
    );
};
